/**
 * `agentPromptsDto.ts` — wire shape of the account's **published agent prompts**:
 * the role, skill, personality and base-prompt Markdown the agent is actually
 * given, plus the workflow schema.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client reads these
 * from `StreamingAssets/AI/**` on disk, where editing one means editing a file.
 * A browser has no writable source tree, so the AI debug panel publishes an
 * edited file here and the catalogs read it in place of the bundled copy.
 *
 * ## The bundled files are the SEED, this document is the source of truth
 *
 * `public/AI/**` ships inside every build. A file with no entry here loads from
 * there — so a fresh account runs the shipped prompts, and "reset" is a real
 * action (delete the entry, fall back to the build's copy) rather than "stop
 * shadowing something".
 *
 * This replaced a debug-gated OVERRIDE layer, and the distinction is the whole
 * point: an override only applied for developers who had typed `potato`, so an
 * uploaded prompt changed nothing for anyone else and the same account ran two
 * different agents depending on who was looking. A published prompt is what the
 * product runs, for everyone on this deployment.
 *
 * ## Per DEPLOYMENT, not per account
 *
 * One document per deployment (`settings/prompts/default.json`,
 * `settings/prompts/jay-testing.json`, …), because a named channel exists to run
 * a DIFFERENT VERSION of the app against the same data — see the deploy script.
 * An account-wide document would shadow a channel's own bundled prompts, which
 * would make a channel useless for the thing prompts are most often channelled
 * to test. The cost, which is deliberate: publishing on one channel does not
 * reach the others, and promoting a prompt means publishing it again on the
 * default deployment.
 *
 * ## Why its own object rather than a key in `app-settings.json`
 *
 * Prompt bodies are kilobytes each and `app-settings.json` is read at boot and
 * REPUBLISHED WHOLE on every settings write (it is a read-modify-write). Keeping
 * bodies out means flipping a toggle does not rewrite every prompt.
 */

/** Which agent-facing content surface a file belongs to. Doubles as the key namespace. */
export type AgentPromptKind = "roles" | "skills" | "personalities" | "base" | "workflow";

export const AGENT_PROMPT_KINDS: ReadonlyArray<AgentPromptKind> = [
  "roles",
  "skills",
  "personalities",
  "base",
  "workflow",
];

/** One published file. */
export interface AgentPromptEntryDto {
  /** The file's full text, exactly as uploaded. */
  readonly text: string;
  /** Epoch ms it was published. */
  readonly uploadedAt: number;
  /** Who published it — the prompt is shared, so "who changed this" is a real question. */
  readonly uploadedBy: string;
}

export interface AgentPromptsDocumentDto {
  readonly version: number;
  /** Keyed `{kind}/{fileName}`, e.g. `skills/answer.md`. */
  readonly prompts: Record<string, AgentPromptEntryDto>;
}

export const AGENT_PROMPTS_SCHEMA_VERSION = 1;

/**
 * The deployment this build belongs to, reduced to a storage-safe segment.
 *
 * The default deployment is named `default` rather than `""` so the key is never
 * `settings/prompts/.json` — a leading dot is also what nginx's dotfile deny
 * would refuse if this ever became a served path.
 *
 * Anything outside `[a-z0-9-]` collapses to `-`: the deploy script already
 * restricts channel names to that class, but `VITE_BASE_PATH` can be set by
 * hand, and a stray `/` or `..` here would write outside the intended prefix.
 */
export function agentPromptsDeploymentKey(channel: string): string {
  const cleaned = channel.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  return cleaned || "default";
}

export function agentPromptsStoragePath(channel: string): string {
  return `settings/prompts/${agentPromptsDeploymentKey(channel)}.json`;
}

/** `{kind}/{fileName}`, or null when either half is missing. */
export function agentPromptKey(kind: AgentPromptKind, fileName: string): string | null {
  const file = normalizeAgentPromptFileName(fileName);
  return file ? `${kind}/${file}` : null;
}

/**
 * Reduces a manifest `file` value to the bare file name.
 *
 * Shared with every catalog on purpose: they key their `fetch` on the basename
 * (a manifest `file` may carry a path), so deriving the storage key any other way
 * would miss for exactly those entries. One derivation, no drift.
 */
export function normalizeAgentPromptFileName(rawFile: string | null | undefined): string {
  if (!rawFile || !rawFile.trim()) return "";
  return rawFile.trim().replace(/\\/g, "/").split("/").pop() ?? "";
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

/**
 * Parses a stored document, dropping anything unusable.
 *
 * A BLANK body is dropped rather than kept: an empty file cannot produce a
 * definition, so keeping one would make every later load of that file fail with
 * no way back except a reset. Writes refuse blanks too — this is the second line.
 */
export function parseAgentPromptsDocument(json: string | null): AgentPromptsDocumentDto {
  const empty: AgentPromptsDocumentDto = {
    version: AGENT_PROMPTS_SCHEMA_VERSION,
    prompts: {},
  };
  if (!json || !json.trim()) return empty;

  let root: unknown;
  try {
    root = JSON.parse(json);
  } catch {
    console.warn("[agentPrompts] prompts document is not valid JSON — treating as empty");
    return empty;
  }

  const parsed = asRecord(root);
  const rawPrompts = asRecord(parsed.prompts);
  const prompts: Record<string, AgentPromptEntryDto> = {};
  for (const [key, value] of Object.entries(rawPrompts)) {
    const entry = asRecord(value);
    const text = typeof entry.text === "string" ? entry.text : "";
    if (!text.trim()) continue;
    prompts[key] = {
      text,
      uploadedAt: typeof entry.uploadedAt === "number" ? entry.uploadedAt : 0,
      uploadedBy: typeof entry.uploadedBy === "string" ? entry.uploadedBy : "",
    };
  }

  return {
    version: typeof parsed.version === "number" ? parsed.version : AGENT_PROMPTS_SCHEMA_VERSION,
    prompts,
  };
}

export function serializeAgentPromptsDocument(doc: AgentPromptsDocumentDto): string {
  return JSON.stringify(
    { version: AGENT_PROMPTS_SCHEMA_VERSION, prompts: doc.prompts },
    null,
    2,
  );
}
